/**
 * Los valores con los que la interfaz se dibuja sobre el lienzo.
 *
 * Existe para que sean UNO. Antes cada archivo de render tenía su propia
 * constante con el mismo azul —`ACCENT` en `CardView` y en `EdgePorts`,
 * `SELECTED_COLOR` en `EdgeLayer`, `DROP_COLOR` en `GroupView`—, que es
 * exactamente cómo se llega a tener cuatro azules distintos sin darse cuenta.
 *
 * La regla del sistema (ver `docs/design-system.md` §1) es que el color es del
 * contenido: la app no tiene color de marca. La excepción, y la única, es la
 * cromática de selección (`UI_SELECTION`).
 */

/**
 * La tinta de la interfaz: lo tomado en una barra, el foco de teclado, las guías
 * del imán. Su gemela en CSS es `--ui-ink` (`styles.css`), con el mismo valor.
 */
export const UI_INK = 0x333333;

/**
 * El azul de sistema: la cromática de selección sobre el lienzo —recuadro,
 * manijas de resize, puertos, destino de un arrastre, marquesina—.
 *
 * Es una excepción deliberada a la regla de arriba, y no es un color de marca:
 * es mobiliario de escritorio. Un recuadro de selección con manijas azules se
 * lee como un objeto del sistema operativo y no como parte del dibujo, que es
 * exactamente lo que tiene que pasar cuando se agarra algo para estirarlo. La
 * tinta hacía el trabajo de contraste pero perdía ese registro.
 *
 * El costo hay que tenerlo presente: es vecino del `#0082F9` de la paleta, así
 * que un objeto dibujado en azul y seleccionado muestra dos azules parecidos
 * con significados distintos. Lo que los separa es la forma —el recuadro tiene
 * manijas, el contenido no— y es la razón por la que este azul no se usa para
 * nada más que la cromática de selección.
 */
export const UI_SELECTION = 0x4c8dff;

/**
 * El papel: el fondo del lienzo, y por eso mismo la funda.
 *
 * La tinta sola no alcanza sobre contenido oscuro —contra el negro de la paleta
 * (`#343331`) da 1.04:1, o sea que desaparece—. Lo que la hace legible sobre
 * cualquier fondo es dibujar dos pasadas: primero una de papel, más gruesa, y la
 * tinta encima. Siempre hay una de las dos que contrasta.
 *
 * Tiene que ser exactamente el fondo del canvas: si fuera más clara, la funda se
 * vería como un halo alrededor de cada trazo en vez de desaparecer contra el
 * lienzo.
 */
export const UI_PAPER = 0xf5f5f5;
